import { useEffect, useState } from 'preact/hooks';
import { platformAudit, AuditRow, QueryAuditRequest } from './api';

export function AuditLogView() {
  const [filters, setFilters] = useState<QueryAuditRequest>({ limit: 50 });
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextBeforeId, setNextBeforeId] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<AuditRow | null>(null);

  async function load(replace: boolean) {
    setLoading(true);
    setErr(null);
    try {
      const req: QueryAuditRequest = { ...filters };
      if (!replace && nextBeforeId) req.beforeId = nextBeforeId;
      const resp = await platformAudit.query(req);
      setRows(replace ? resp.rows : [...rows, ...resp.rows]);
      setHasMore(resp.hasMore);
      setNextBeforeId(resp.nextBeforeId || 0);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyFilters(e: Event) {
    e.preventDefault();
    setRows([]);
    setNextBeforeId(0);
    load(true);
  }

  return (
    <div>
      <h1>Audit log</h1>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Every mutation + sensitive read performed by a platform user is recorded here,
        append-only. Monthly-partitioned, never deleted.
      </p>

      <form
        onSubmit={applyFilters}
        style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}
      >
        <input
          placeholder="Action prefix (e.g. login_*, billing.*)"
          value={filters.actionPrefix || ''}
          onInput={(e) =>
            setFilters({ ...filters, actionPrefix: (e.target as HTMLInputElement).value })
          }
          style={{ flex: 1, minWidth: 220 }}
        />
        <input
          placeholder="Target kind (e.g. org, platform_user)"
          value={filters.targetKind || ''}
          onInput={(e) =>
            setFilters({ ...filters, targetKind: (e.target as HTMLInputElement).value })
          }
          style={{ width: 200 }}
        />
        <select
          value={filters.success || 'SUCCESS_FILTER_UNSPECIFIED'}
          onChange={(e) =>
            setFilters({ ...filters, success: (e.target as HTMLSelectElement).value as any })
          }
          style={{ width: 160 }}
        >
          <option value="SUCCESS_FILTER_UNSPECIFIED">Any outcome</option>
          <option value="SUCCESS_FILTER_ONLY_SUCCESS">Success only</option>
          <option value="SUCCESS_FILTER_ONLY_FAILURE">Failures only</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="checkbox"
            checked={Boolean(filters.impersonationOnly)}
            onChange={(e) =>
              setFilters({
                ...filters,
                impersonationOnly: (e.target as HTMLInputElement).checked,
              })
            }
          />
          <span style={{ fontSize: 13, color: 'var(--admin-text-dim)' }}>
            Impersonation only
          </span>
        </label>
        <button class="primary" type="submit" disabled={loading}>
          Apply
        </button>
      </form>

      {err && <div class="error-banner">{err}</div>}

      <table class="admin-table">
        <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            <th>Action</th>
            <th>Target</th>
            <th>Outcome</th>
            <th>From</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              onClick={() => setSelected(r)}
              style={{ cursor: 'pointer' }}
            >
              <td class="mono">{formatTime(r.occurredAt)}</td>
              <td>{r.platformUsername || <span style={{ color: 'var(--admin-text-dim)' }}>—</span>}</td>
              <td class="mono">{r.action}</td>
              <td class="mono">
                {r.targetKind}
                {r.targetId && <>: {truncate(r.targetId, 30)}</>}
              </td>
              <td>
                <span class={`badge ${r.success ? 'success' : 'failure'}`}>
                  {r.success ? 'ok' : 'fail'}
                </span>
              </td>
              <td class="mono">{r.requestIp || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length === 0 && !loading && (
        <p style={{ color: 'var(--admin-text-dim)' }}>No rows match.</p>
      )}

      <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
        {hasMore && (
          <button onClick={() => load(false)} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        )}
      </div>

      {selected && <AuditRowDetail row={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function AuditRowDetail({ row, onClose }: { row: AuditRow; onClose(): void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--admin-surface)',
          border: '1px solid var(--admin-border)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 820,
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 style={{ margin: 0 }}>Audit entry #{row.id}</h2>
          <button onClick={onClose}>Close</button>
        </div>
        <p class="mono" style={{ color: 'var(--admin-text-dim)' }}>
          {formatTime(row.occurredAt)} · {row.action} ·{' '}
          <span class={`badge ${row.success ? 'success' : 'failure'}`}>
            {row.success ? 'ok' : 'fail'}
          </span>
        </p>

        <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 16px' }}>
          {row.platformUserId && (
            <>
              <dt style={{ color: 'var(--admin-text-dim)' }}>Platform user</dt>
              <dd class="mono" style={{ margin: 0 }}>{row.platformUsername} ({row.platformUserId})</dd>
            </>
          )}
          {row.impersonatedById && (
            <>
              <dt style={{ color: 'var(--admin-text-dim)' }}>Impersonated by</dt>
              <dd class="mono" style={{ margin: 0 }}>{row.impersonatedById}</dd>
            </>
          )}
          {row.targetKind && (
            <>
              <dt style={{ color: 'var(--admin-text-dim)' }}>Target</dt>
              <dd class="mono" style={{ margin: 0 }}>
                {row.targetKind}
                {row.targetId && <>: {row.targetId}</>}
              </dd>
            </>
          )}
          {row.requestIp && (
            <>
              <dt style={{ color: 'var(--admin-text-dim)' }}>IP</dt>
              <dd class="mono" style={{ margin: 0 }}>{row.requestIp}</dd>
            </>
          )}
          {row.requestUserAgent && (
            <>
              <dt style={{ color: 'var(--admin-text-dim)' }}>User agent</dt>
              <dd class="mono" style={{ margin: 0, wordBreak: 'break-all' }}>{row.requestUserAgent}</dd>
            </>
          )}
          {row.errorMessage && (
            <>
              <dt style={{ color: 'var(--admin-danger)' }}>Error</dt>
              <dd style={{ margin: 0, color: 'var(--admin-danger)' }}>{row.errorMessage}</dd>
            </>
          )}
        </dl>

        {row.beforeStateJson && (
          <>
            <h3>Before</h3>
            <pre>{prettyJSON(row.beforeStateJson)}</pre>
          </>
        )}
        {row.afterStateJson && (
          <>
            <h3>After</h3>
            <pre>{prettyJSON(row.afterStateJson)}</pre>
          </>
        )}
        {row.metadataJson && (
          <>
            <h3>Metadata</h3>
            <pre>{prettyJSON(row.metadataJson)}</pre>
          </>
        )}
      </div>
    </div>
  );
}

function formatTime(t: string): string {
  try {
    return new Date(t).toLocaleString();
  } catch {
    return t;
  }
}
function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + '…' : s;
}
function prettyJSON(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
